import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Row, Col, Form } from 'antd';
import { I18n } from '@aws-amplify/core';

import { FilterForm } from './Form';
import { TableListForm } from '../List/components';
import { useDataExtractionReport } from '@/context/DataExtractionReport';
import { useGetCustomReportByTypeList } from '@/hooks';
import { useHandleReports } from '@/components/Settings/DataExtraction/ExtractionType/Hierarchy/Steps/Reports/hooks/useHandleReports';
import { Pagination } from '@/components/ui/Pagination';
import { FilterFormSubmitValues } from '@/components/Settings/DataExtraction/ExtractionType/Hierarchy/types';
import * as S from '@/components/Settings/DataExtraction/styles';

const { useForm, useWatch } = Form;

interface FilterState {
	organization_id: string;
	company_id: string;
	sector_id: string;
	line_id: string;
	workstation_id: string;
	min_rpn: number;
	max_rpn: number;
	start_date: string;
	end_date: string;
	offset: number;
	limit: number;
	custom_report_id: string;
	data_extraction_id?: string;
}

export function Filter() {
	const [form] = useForm<FilterFormSubmitValues>();
	const { dataExtractionReport } = useDataExtractionReport();
	const reportTypeId: string | undefined = useWatch(['reports', 'custom_report_type_id']);

	const organization_id = dataExtractionReport?.organization_id;
	const company_id = dataExtractionReport?.company_id;
	const sector_id = dataExtractionReport?.sector_id;
	const line_id = dataExtractionReport?.line_id;
	const workstation_id = dataExtractionReport?.workstation_id;

	const createDefaultFilters = useCallback((): FilterState => {
		return {
			custom_report_id: reportTypeId!,
			organization_id: organization_id || '',
			company_id: company_id || '',
			sector_id: sector_id || '',
			line_id: line_id || '',
			workstation_id: workstation_id || '',
			min_rpn: 0,
			max_rpn: 0,
			start_date: '',
			end_date: '',
			offset: 1,
			limit: 10,
			data_extraction_id: dataExtractionReport?.id
		};
	}, [reportTypeId, organization_id, company_id, sector_id, line_id, workstation_id, dataExtractionReport?.id]);

	const defaultFilters = useMemo(() => createDefaultFilters(), [createDefaultFilters]);
	const initialValues = defaultFilters;
	const [filters, setFilters] = useState<FilterState>(createDefaultFilters);

	const apiFilters = useMemo(() => {
		const { custom_report_id, offset, limit, data_extraction_id, organization_id, company_id, ...optionalFields } =
			filters;
		const cleanOptional = Object.fromEntries(Object.entries(optionalFields).filter(([_, value]) => Boolean(value)));

		return {
			custom_report_id,
			organization_id,
			company_id,
			offset,
			limit,
			...cleanOptional,
			...(data_extraction_id && { data_extraction_id })
		};
	}, [filters]);

	const { isLoading, data: customReportByTypeList } = useGetCustomReportByTypeList(apiFilters);
	const { selectedReportsCount } = useHandleReports([], customReportByTypeList?.total);

	function updateFilters(data: Partial<FilterState & FilterFormSubmitValues>): void {
		const cleanData = Object.fromEntries(Object.entries(data).filter(([_, value]) => Boolean(value)));
		setFilters((prev) => ({ ...prev, ...cleanData }));
	}

	function resetFilters(): void {
		setFilters(createDefaultFilters());
	}

	useEffect(() => {
		if (reportTypeId) {
			updateFilters({ custom_report_id: reportTypeId });
		}
	}, [reportTypeId]);

	function handleOnChangePage(offset: number): void {
		updateFilters({ offset });
	}

	function handleOnChangeLimit(limit: number): void {
		updateFilters({ limit, offset: 1 });
	}

	function handleOnSubmit(values: FilterFormSubmitValues): void {
		updateFilters(values);
	}

	function handleOnClose(): void {
		resetFilters();
		form.resetFields();
	}

	if (!reportTypeId) {
		return null;
	}

	return (
		<Row gutter={[0, 16]}>
			<Col span={24}>
				<S.BodyLargeRegular>
					{I18n.get('Select the reports you want to include in the extraction')}
				</S.BodyLargeRegular>
			</Col>
			<Col span={24}>
				<Form form={form} initialValues={initialValues}>
					<Row gutter={[0, 24]}>
						<Col span={24}>
							<FilterForm form={form} onSubmit={handleOnSubmit} onClose={handleOnClose} />
						</Col>
						<Col span={24}>
							<TableListForm
								loading={isLoading}
								data={customReportByTypeList?.rows}
								selectedReportIds={customReportByTypeList?.selected_report_ids?.report_ids || []}
								totalReportsSelected={selectedReportsCount}
							/>
						</Col>

						<Col span={24}>
							<Pagination
								loading={isLoading}
								total={customReportByTypeList?.total}
								page={filters.offset}
								limit={filters.limit}
								onSetLimit={handleOnChangeLimit}
								onSetPage={handleOnChangePage}
							/>
						</Col>
					</Row>
				</Form>
			</Col>
		</Row>
	);
}
